import type { VestiaireFormState } from '@/features/productSearch/vestiaire/vestiaireCriteria';
import type { AnalysisSellerRegion } from '@/shared/api/types/generated';
import type { SelectOption } from '@/shared/types/options';

// ============================================================================
// Mise en forme pure des listes d'options Vestiaire (matières, pays) pour le
// panneau de filtres — séparée du composant pour être testée.
// ============================================================================

// ⚠️ Sans `limit`, le serveur borne toute liste à 50 options (DEFAULT_VESTIAIRE_OPTIONS_LIMIT) :
// le dictionnaire VC compte 56 matières, 6 n'étaient jamais proposées. 100 = maximum serveur.
// Partagé par toutes les lectures de liste complète (même clé de cache).
export const VESTIAIRE_FULL_LIST_LIMIT = 100;

export interface VestiaireOptionLike {
  id: number;
  title: string;
  parentId?: number | null;
}

/**
 * Matières à deux niveaux : un enfant s'affiche `parent › enfant` (parent cherché dans la même
 * liste par `parentId`) ; un parent absent de la liste laisse le titre seul. Ordre serveur conservé.
 */
export const toMaterialOptions = (options: VestiaireOptionLike[]): SelectOption[] => {
  const titleById = new Map(options.map((option) => [option.id, option.title]));
  return options.map((option) => {
    const parentTitle = option.parentId != null ? titleById.get(option.parentId) : undefined;
    return {
      value: String(option.id),
      label: parentTitle !== undefined ? `${parentTitle} › ${option.title}` : option.title,
    };
  });
};

/**
 * Niveau VC (0 ou 1) de chaque matière, pour écrire son id sous la bonne clé de l'URL VC. `level`
 * fait foi ; à défaut, un `parentId` désigne une matière de niveau 1. Sans l'un ni l'autre, la
 * matière est absente de la table (niveau inconnu, jamais deviné).
 */
export const materialLevelsById = (
  options: readonly (VestiaireOptionLike & { level?: number | null })[],
): ReadonlyMap<number, number> => {
  const levels = new Map<number, number>();
  for (const option of options) {
    const level = option.level ?? (option.parentId != null ? 1 : null);
    if (level != null) levels.set(option.id, level);
  }
  return levels;
};

/** Ordre d'affichage des régions (même enum que Vinted, `others` en dernier). */
export const VESTIAIRE_REGION_ORDER = [
  'europeWest',
  'unitedKingdom',
  'europeEast',
  'northAmerica',
  'others',
] as const satisfies readonly AnalysisSellerRegion[];

export type VestiaireRegion = (typeof VESTIAIRE_REGION_ORDER)[number];

export interface VestiaireCountryLike {
  id: number;
  name: string;
  region: AnalysisSellerRegion;
  sellerCount: number;
}

export interface VestiaireCountryGroup<T extends VestiaireCountryLike> {
  region: VestiaireRegion;
  countries: T[];
}

const isKnownRegion = (region: AnalysisSellerRegion): region is VestiaireRegion =>
  (VESTIAIRE_REGION_ORDER as readonly string[]).includes(region);

/**
 * Pays groupés par région dans l'ordre fixe, triés par `sellerCount` décroissant (puis par nom)
 * dans chaque groupe ; groupes vides retirés. Une région inconnue du front (valeur future de
 * l'enum) tombe dans `others` plutôt que de faire disparaître le pays.
 */
export const groupCountriesByRegion = <T extends VestiaireCountryLike>(countries: T[]): VestiaireCountryGroup<T>[] =>
  VESTIAIRE_REGION_ORDER.map((region) => ({
    region,
    countries: countries
      .filter((country) => (isKnownRegion(country.region) ? country.region : 'others') === region)
      .sort((a, b) => b.sellerCount - a.sellerCount || a.name.localeCompare(b.name)),
  })).filter((group) => group.countries.length > 0);

/**
 * Sections du panneau (clés des `Accordion.Item`) qui portent une valeur : ouvertes d'office en
 * lecture seule (édition, « voir les filtres »), comme le panneau Vinted.
 */
export const filledVestiaireSections = (form: VestiaireFormState): string[] => {
  const sections: [string, boolean][] = [
    ['categories', form.catalogPaths.length > 0],
    ['brands', form.brandIds.length > 0 || form.excludedBrandIds.length > 0],
    ['models', form.modelIds.length > 0],
    ['conditionIds', form.conditionIds.length > 0],
    ['sizes', form.sizeIds.length > 0],
    ['colorIds', form.colorIds.length > 0],
    ['materialIds', form.materialIds.length > 0],
    ['countries', form.countryIds.length > 0],
    ['sellerBadges', form.sellerBadges.length > 0],
    ['itemBadges', form.itemBadges.length > 0],
    ['price', form.priceMin.trim() !== '' || form.priceMax.trim() !== ''],
    ['keywords', form.keywords.length > 0 || form.excludedKeywords.length > 0],
  ];
  return sections.filter(([, filled]) => filled).map(([key]) => key);
};
